import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Rating } from "primereact/rating";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import "./userfeedbacks.css";

export default function UserFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filters, setFilters] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/userFeedback/loadfeedback`)
      .then((res) => {
        const enrichedData = res.data.map((fb) => {
          const products = (fb.itemRatings || [])
            .map((item) => item.productTitle)
            .filter(Boolean);
          return {
            ...fb,
            joinedProductTitles: products.join(", "),
          };
        });
        setFeedbacks(enrichedData);
        initFilters();
        setLoading(false);
      })
      .catch(() => {
        toast.error("❌ Failed to load feedback data");
        setLoading(false);
      });
  }, []);

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      orderId: { value: null, matchMode: FilterMatchMode.CONTAINS },
      comment: { value: null, matchMode: FilterMatchMode.CONTAINS },
      overallRating: { value: null, matchMode: FilterMatchMode.EQUALS },
      joinedProductTitles: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const itemRatingsTemplate = (rowData) => {
    if (!rowData.itemRatings || rowData.itemRatings.length === 0) {
      return <Tag value="No Ratings" severity="info" />;
    }

    return (
      <div className="item-rating-group">
        {rowData.itemRatings.map((item, index) => (
          <div key={index} className="item-rating-row">
            <span className="item-name">{item.productTitle}</span>
            <div className={getRatingColor(item.rating)}>
              <Rating value={item.rating} readOnly cancel={false} stars={5} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const overallRatingFilterTemplate = (options) => {
    const ratingOptions = [1, 2, 3, 4, 5].map((star) => ({
      label: `${star} Stars`,
      value: star,
    }));

    return (
      <Dropdown
        value={options.value}
        options={ratingOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select Stars"
        className="p-column-filter"
        showClear
        itemTemplate={(option) => (
          <div className="flex align-items-center">
            <Rating value={option.value} readOnly cancel={false} stars={5} />
          </div>
        )}
        optionLabel="label"
      />
    );
  };

  const tableHeader = (
    <div className="flex justify-content-end mb-3">
      <InputText
        value={globalFilter}
        onChange={(e) => {
          setGlobalFilter(e.target.value);
          setFilters((prev) => ({
            ...prev,
            global: {
              value: e.target.value,
              matchMode: FilterMatchMode.CONTAINS,
            },
          }));
        }}
        placeholder="Search feedback..."
        className="w-full md:w-25rem"
      />
    </div>
  );

  const getRatingColor = (rating) => {
    if (rating >= 5) return "rating-green";
    if (rating >= 3) return "rating-blue";
    return "rating-red";
  };

  // COMMENT COLUMN: Show only first 3 words or 30 chars, show "More" if longer
  const commentBodyTemplate = (rowData) => {
    const comment = rowData.comment || "";
    const words = comment.trim().split(/\s+/);
    let displayText = comment;
    let showMore = false;

    if (words.length > 3) {
      displayText = words.slice(0, 3).join(" ");
      showMore = true;
    }
    if (displayText.length > 30) {
      displayText = displayText.slice(0, 30);
      showMore = true;
    }

    return (
      <span data-label="Comment" className="truncate-comment">
        {displayText}
        {showMore && (
          <>
            ...{" "}
            <button
              type="button"
              className="more-btn"
              onClick={() => {
                setSelectedComment(comment);
                setShowCommentModal(true);
              }}
            >
              More
            </button>
          </>
        )}
      </span>
    );
  };

  return (
    <div className="p-4">
      <h2 className="mb-3">User Feedback List</h2>

      <DataTable
        value={feedbacks}
        paginator
        rows={10}
        loading={loading}
        filters={filters}
        globalFilterFields={["orderId", "comment", "joinedProductTitles"]}
        onFilter={(e) => setFilters(e.filters)}
        filterDisplay="row"
        header={tableHeader}
        emptyMessage="No feedbacks found."
        className="p-datatable-sm"
        responsiveLayout="scroll"
      >
        <Column
          field="orderId"
          header="Order ID"
          filter
          filterPlaceholder="Search by Order ID"
          showFilterMenu={false}
          style={{ minWidth: "180px" }}
          body={(rowData) => <div data-label="Order ID">{rowData.orderId}</div>}
        />

        <Column
          field="overallRating"
          header="Overall Rating"
          filter
          filterElement={overallRatingFilterTemplate}
          showFilterMenu={false}
          style={{ minWidth: "180px" }}
          body={(rowData) => (
            <div data-label="Overall Rating" className={getRatingColor(rowData.overallRating)}>
              <Rating
                value={rowData.overallRating}
                readOnly
                cancel={false}
                stars={5}
              />
            </div>
          )}
        />

        <Column
          field="comment"
          header="Comment"
          filter
          filterPlaceholder="Search by Comment"
          showFilterMenu={false}
          style={{ minWidth: "250px" }}
          body={commentBodyTemplate}
        />

        <Column
          field="joinedProductTitles"
          header="Item Ratings"
          filter
          filterPlaceholder="Search by Product Name"
          showFilterMenu={false}
          style={{ minWidth: "300px" }}
          body={(rowData) => (
            <div data-label="Item Ratings">
              {itemRatingsTemplate(rowData)}
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header="Full Comment"
        visible={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        style={{ width: "400px", maxWidth: "90vw" }}
        modal
        className="comment-dialog"
      >
        <div className="dialog-content">{selectedComment}</div>
        <div className="dialog-footer">
          
        </div>
      </Dialog>
    </div>
  );
}